import { FormEvent, useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { Modal } from './ui/modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { TenantDetailsDto, TenantFeaturesDto, UpdateTenantRequest } from '../models';
import { getApiError, getTenant, getTenantFeatures, updateTenant, updateTenantFeatures } from '../services/api';

const disabledFeatures: TenantFeaturesDto = {
  medicalCheckupQueue: false,
  crossTenantFallback: false,
  profileEditing: false,
  branding: false,
  advancedCompanyManagement: false,
};

interface Props {
  tenantId?: number;
  onClose: () => void;
  onSaved: (name: string) => void;
}

export const EditTenantDialog = ({ tenantId, onClose, onSaved }: Props) => {
  const [value, setValue] = useState<TenantDetailsDto>();
  const [features, setFeatures] = useState<TenantFeaturesDto>(disabledFeatures);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    setValue(undefined);
    setError('');
    Promise.all([getTenant(tenantId), getTenantFeatures(tenantId)])
      .then(([tenant, tenantFeatures]) => {
        setValue(tenant.data.data);
        setFeatures(tenantFeatures.data.data);
      })
      .catch((e) => setError(getApiError(e)));
  }, [tenantId]);

  const update = (field: keyof UpdateTenantRequest) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue((c) => c ? { ...c, [field]: newValue } : c);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!tenantId || !value) return;
    setSaving(true);
    setError('');
    try {
      const { id, ...request } = value;
      await updateTenant(id, {
        ...request,
        licenceSerial: request.licenceSerial?.trim() || null,
        licenceNumber: request.licenceNumber?.trim() || null,
        licenceIssuedAt: request.licenceIssuedAt || null,
        licenceExpiredAt: request.licenceExpiredAt || null,
      });
      await updateTenantFeatures(id, {
        ...features,
        crossTenantFallback: features.medicalCheckupQueue && features.crossTenantFallback,
      });
      onSaved(value.tenantName);
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={Boolean(tenantId)} onClose={saving ? () => {} : onClose} title="Редактирование организации" maxWidth="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Отмена</Button>
          <Button type="submit" form="edit-tenant-form" disabled={!value || saving}>
            {saving ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" /> : <Save className="h-4 w-4" />}
            Сохранить
          </Button>
        </>
      }
    >
      {!value && !error ? <div className="flex justify-center py-8"><div className="h-7 w-7 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div> : null}
      {error && <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {value && (
        <form id="edit-tenant-form" onSubmit={submit} className="space-y-4">
          <Input required label="Название медорганизации" value={value.tenantName} onChange={update('tenantName')} maxLength={50} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input required label="ИНН" value={value.inn} onChange={update('inn')} />
            <div className="sm:col-span-2">
              <Input required label="Адрес" value={value.address} onChange={update('address')} />
            </div>
          </div>
          <div className="border-t border-border" />
          <div>
            <h4 className="mb-1 text-sm font-semibold text-foreground">Лицензия медицинской организации</h4>
            <p className="mb-3 text-xs text-muted-foreground">Серия, номер и дата выдачи нужны для отправки вердикта на терминал.</p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input label="Серия лицензии" value={value.licenceSerial ?? ''} onChange={update('licenceSerial')} maxLength={15} />
              <Input label="Номер лицензии" value={value.licenceNumber ?? ''} onChange={update('licenceNumber')} maxLength={50} />
              <Input type="date" label="Дата выдачи" value={value.licenceIssuedAt ?? ''} onChange={update('licenceIssuedAt')} />
              <Input type="date" label="Дата окончания" value={value.licenceExpiredAt ?? ''} onChange={update('licenceExpiredAt')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Input required label="Название продукта" value={value.productName} onChange={update('productName')} />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="edit-primary-color" className="text-sm font-medium text-foreground">Основной цвет</label>
              <input id="edit-primary-color" type="color" value={value.primaryColor} onChange={update('primaryColor')} className="h-10 w-full cursor-pointer rounded-lg border border-border bg-background" />
            </div>
          </div>
          <div className="border-t border-border" />
          <div>
            <h4 className="mb-1 text-sm font-semibold text-foreground">Функции для этого клиента</h4>
            <p className="mb-3 text-xs text-muted-foreground">Новые функции по умолчанию выключены. Очередь и переброс можно включать отдельно.</p>
            <div className="space-y-2 rounded-lg border border-border p-3">
              <FeatureSwitch label="Очередь медосмотров" checked={features.medicalCheckupQueue} onChange={(checked) => setFeatures((current) => ({ ...current, medicalCheckupQueue: checked, crossTenantFallback: checked && current.crossTenantFallback }))} />
              <FeatureSwitch label="Переброс между медорганизациями" checked={features.crossTenantFallback} disabled={!features.medicalCheckupQueue} onChange={(checked) => setFeatures((current) => ({ ...current, crossTenantFallback: checked }))} />
              <FeatureSwitch label="Редактирование профиля" checked={features.profileEditing} onChange={(checked) => setFeatures((current) => ({ ...current, profileEditing: checked }))} />
              <FeatureSwitch label="Брендинг личного кабинета" checked={features.branding} onChange={(checked) => setFeatures((current) => ({ ...current, branding: checked }))} />
              <FeatureSwitch label="Расширенное управление клиентами" checked={features.advancedCompanyManagement} onChange={(checked) => setFeatures((current) => ({ ...current, advancedCompanyManagement: checked }))} />
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
};

const FeatureSwitch = ({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) => (
  <label className={`flex items-center justify-between gap-4 text-sm ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
    <span>{label}</span>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-primary" />
  </label>
);
